import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, openAuth } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('home');
  const [notifications, setNotifications] = useState({
    home: false,
    offers: false,
    ai: false,
    friends: false
  });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [hasNewScholarships, setHasNewScholarships] = useState(false);
  const scrollTimerRef = useRef(null);
  const activeTabTimerRef = useRef(null);

  // Check for new scholarships (shows notification dot)
  useEffect(() => {
    const checkNewScholarships = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${API_URL}/api/scholarships/last-update`);
        const data = await res.json();
        if (data.last_updated) {
          const lastSeen = localStorage.getItem('scholarships_last_seen');
          if (!lastSeen || new Date(data.last_updated) > new Date(lastSeen)) {
            setHasNewScholarships(true);
          }
        }
      } catch {}
    };
    checkNewScholarships();
    const timer = setInterval(checkNewScholarships, 30 * 60 * 1000); // every 30 min
    return () => clearInterval(timer);
  }, []);

  // When CV-triggered search finishes (~60s), light up the notification dot
  // if the user has navigated away from the scholarships page
  useEffect(() => {
    const handleCvDone = () => {
      // Only show dot if user is NOT already on the scholarships page
      if (!window.location.pathname.includes('scholarships')) {
        setHasNewScholarships(true);
      }
    };
    window.addEventListener('scholarships_cv_done', handleCvDone);
    return () => window.removeEventListener('scholarships_cv_done', handleCvDone);
  }, []);

  // Determine active tab based on current route and auto-deactivate after 10 seconds
  useEffect(() => {
    const path = location.pathname;
    
    // Clear existing timer
    if (activeTabTimerRef.current) {
      clearTimeout(activeTabTimerRef.current);
    }
    
    // Set active tab based on route
    if (path === '/' || path === '/home') {
      setActiveTab('home');
      setNotifications(prev => ({ ...prev, home: false }));
    } else if (path.includes('scholarships')) {
      setActiveTab('offers');
      setHasNewScholarships(false);
      setNotifications(prev => ({ ...prev, offers: false }));
      localStorage.setItem('scholarships_last_seen', new Date().toISOString());
    } else if (path.includes('ai-advisor')) {
      setActiveTab('ai');
      setNotifications(prev => ({ ...prev, ai: false }));
    } else if (path.includes('friends-chat')) {
      setActiveTab('friends');
      setNotifications(prev => ({ ...prev, friends: false }));
    } else {
      setActiveTab('home');
    }
    
    // Set timer to deactivate after 10 seconds
    activeTabTimerRef.current = setTimeout(() => {
      setActiveTab('');
    }, 10000);
    
    // Cleanup
    return () => {
      if (activeTabTimerRef.current) {
        clearTimeout(activeTabTimerRef.current);
      }
    };
  }, [location.pathname]);

  // Function to be called from backend/websocket for new activity
  window.newActivity = (type) => {
    const tabMap = { home: 'home', offers: 'offers', ai: 'ai', friends: 'friends' };
    const tab = tabMap[type];
    if (tab && activeTab !== tab) {
      setNotifications(prev => ({ ...prev, [tab]: true }));
    }
  };

  // Listen for friends chat open/close events
  useEffect(() => {
    const handleChatOpen = () => setIsChatOpen(true);
    const handleChatClose = () => setIsChatOpen(false);

    window.addEventListener('openFriendsChat', handleChatOpen);
    window.addEventListener('closeFriendsChat', handleChatClose);

    return () => {
      window.removeEventListener('openFriendsChat', handleChatOpen);
      window.removeEventListener('closeFriendsChat', handleChatClose);
    };
  }, []);

  // Listen for auth modal open/close events
  useEffect(() => {
    const handleAuthOpen = () => setIsAuthOpen(true);
    const handleAuthClose = () => setIsAuthOpen(false);

    window.addEventListener('openAuthModal', handleAuthOpen);
    window.addEventListener('closeAuthModal', handleAuthClose);

    return () => {
      window.removeEventListener('openAuthModal', handleAuthOpen);
      window.removeEventListener('closeAuthModal', handleAuthClose);
    };
  }, []);

  // Scroll-based auto-hide (only on pages where bar is normally visible)
  useEffect(() => {
    const handleScroll = () => {
      // Only apply scroll behavior if bar is not already hidden by other conditions
      if (!shouldHide) {
        setIsScrolling(true);

        // Clear existing timer
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }

        // Set new timer - show bar after 1 second of no scrolling
        scrollTimerRef.current = setTimeout(() => {
          setIsScrolling(false);
        }, 1000);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [shouldHide]);

  // Combined hide logic: Ask AI, Learn Research, Samhara AI, Career Ambition pages OR Friends chat open OR Auth modal open
  useEffect(() => {
    const isAskAIPage = location.pathname.includes('/ai-advisor');
    const isLearnResearchPage = location.pathname.includes('/learn-research');
    const isSamharaAIPage = location.pathname.includes('/samhara-ai');
    const isCareerAmbitionPage = location.pathname.includes('/career-ambition');
    setShouldHide(isAskAIPage || isLearnResearchPage || isSamharaAIPage || isCareerAmbitionPage || isChatOpen || isAuthOpen);
  }, [location.pathname, isChatOpen, isAuthOpen]);

  const handleTabClick = (tab, route) => {
    // Check authentication for protected routes
    if (tab !== 'home' && !user) {
      openAuth('login'); // Open Sign In window
      return;
    }
    
    setActiveTab(tab);
    navigate(route);
  };

  const handleFriendsClick = () => {
    // Check authentication before opening Friends chat
    if (!user) {
      openAuth('login'); // Open Sign In window
      return;
    }
    
    setActiveTab('friends');
    // Clear notification
    setNotifications(prev => ({ ...prev, friends: false }));
    
    // Trigger the friends chat popup by dispatching custom event
    window.dispatchEvent(new CustomEvent('openFriendsChat'));
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800&display=swap');
        
        .float-nav {
          position: fixed;
          bottom: calc(22px + env(safe-area-inset-bottom));
          left: 12px;
          right: 12px;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-top: 1.5px solid rgba(255,255,255,0.2);
          border-left: 1.5px solid rgba(255,255,255,0.15);
          border-right: 1.5px solid rgba(255,255,255,0.1);
          border-bottom: 1.5px solid rgba(255,255,255,0.05);
          box-shadow: 
            0 4px 24px rgba(0,0,0,0.1),
            inset 0 1px 0 rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 14px 6px 14px;
          display: flex;
          justify-content: center;
          gap: 14px;
          align-items: center;
          z-index: 9999;
          animation: slideUp 0.6s cubic-bezier(0.34, 1.4, 0.64, 1);
          font-family: 'Nunito', sans-serif;
          transition: opacity 0.3s ease, transform 0.3s ease;
        }

        /* Hidden state - slides down smoothly */
        .float-nav.hidden {
          opacity: 0;
          transform: translateY(150%);
          pointer-events: none;
        }

        @keyframes slideUp {
          from {
            transform: translateY(150%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .nav-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          min-height: 56px;
          min-width: 60px;
          flex: 1;
          position: relative;
          transition: transform 0.1s ease;
        }

        .nav-btn:active {
          transform: scale(0.92);
        }

        .icon-bubble {
          width: 44px;
          height: 44px;
          min-width: 44px;
          min-height: 44px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          background: rgba(255, 255, 255, 0.35);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.3);
          overflow: hidden;
        }

        .icon-bubble.active {
          transform: translateY(-6px);
        }

        /* Active glow shadows - lit from within effect */
        .icon-bubble.active.home {
          box-shadow: 0 6px 20px rgba(13,18,33,0.5);
        }

        .icon-bubble.active.offers {
          box-shadow: 0 6px 20px rgba(245,197,24,0.5);
        }

        .icon-bubble.active.ai {
          box-shadow: 0 6px 20px rgba(14,165,176,0.45);
        }

        .icon-bubble.active.friends {
          box-shadow: 0 6px 20px rgba(249,115,22,0.5);
        }

        .icon-bubble.inactive {
          /* Keep frosted glass from base style */
        }

        /* Inactive icons with colored glows */
        .icon-bubble.inactive.home-inactive svg {
          stroke: rgba(13,18,33,0.9);
          filter: drop-shadow(0 0 5px rgba(13,18,33,0.6));
        }

        .icon-bubble.inactive.offers-inactive svg {
          filter: drop-shadow(0 0 6px rgba(245,197,24,0.9));
        }

        .icon-bubble.inactive.ai-inactive svg {
          stroke: rgba(14,165,176,0.9);
          filter: drop-shadow(0 0 5px rgba(14,165,176,0.7));
        }

        .icon-bubble.inactive.friends-inactive svg {
          stroke: rgba(249,115,22,0.9);
          filter: drop-shadow(0 0 5px rgba(249,115,22,0.7));
        }

        /* Active gradients - "tablet depth" effect */
        .icon-bubble.active.home {
          background: linear-gradient(180deg, #1a2d4a 0%, #0d1221 100%);
          border-top: 1.5px solid rgba(255, 255, 255, 0.15);
          border-left: 1.5px solid rgba(255, 255, 255, 0.1);
          border-right: 1.5px solid rgba(0, 0, 0, 0.3);
          border-bottom: 3px solid rgba(0, 0, 0, 0.5);
        }

        .icon-bubble.active.offers {
          background: linear-gradient(180deg, #f5c518 0%, #d4a00a 100%);
          border-top: 1.5px solid rgba(255, 255, 255, 0.4);
          border-left: 1.5px solid rgba(255, 255, 255, 0.3);
          border-right: 1.5px solid rgba(0, 0, 0, 0.2);
          border-bottom: 3px solid rgba(0, 0, 0, 0.3);
        }

        .icon-bubble.active.ai {
          background: linear-gradient(180deg, #0ea5b0 0%, #0d7a85 100%);
          border-top: 1.5px solid rgba(255, 255, 255, 0.3);
          border-left: 1.5px solid rgba(255, 255, 255, 0.2);
          border-right: 1.5px solid rgba(0, 0, 0, 0.25);
          border-bottom: 3px solid rgba(0, 0, 0, 0.4);
        }

        .icon-bubble.active.friends {
          background: linear-gradient(180deg, #f97316 0%, #ea6000 100%);
          border-top: 1.5px solid rgba(255, 255, 255, 0.35);
          border-left: 1.5px solid rgba(255, 255, 255, 0.25);
          border-right: 1.5px solid rgba(0, 0, 0, 0.25);
          border-bottom: 3px solid rgba(0, 0, 0, 0.35);
        }

        /* Silver shimmer on active */
        .icon-bubble.active::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 50%,
            transparent 100%
          );
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) rotate(45deg);
          }
        }

        .nav-label {
          font-size: 10px;
          font-weight: 700;
          white-space: nowrap;
          text-align: center;
          transition: color 0.2s ease;
        }

        .nav-label.active {
          color: #f97316;
        }

        /* Inactive labels - color matched per tab */
        .nav-label.inactive.home-label {
          color: rgba(255,255,255,0.9);
        }

        .nav-label.inactive.offers-label {
          color: rgba(212,160,10,0.9);
        }

        .nav-label.inactive.ai-label {
          color: rgba(14,165,176,0.9);
        }

        .nav-label.inactive.friends-label {
          color: rgba(249,115,22,0.9);
        }

        /* Notification dot — sits on top-right corner of the icon */
        .notification-dot {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 9px;
          height: 9px;
          border: 1.5px solid #fff;
          border-radius: 50%;
          z-index: 10;
        }

        .notification-dot::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: pulse-ring 1.6s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
        }

        /* Per-tab dot colours */
        .notification-dot.dot-home        { background: #0d1221; }
        .notification-dot.dot-home::before { background: #0d1221; }

        .notification-dot.dot-offers        { background: #f5c518; }
        .notification-dot.dot-offers::before { background: #f5c518; }

        .notification-dot.dot-ai        { background: #0ea5b0; }
        .notification-dot.dot-ai::before { background: #0ea5b0; }

        .notification-dot.dot-friends        { background: #f97316; }
        .notification-dot.dot-friends::before { background: #f97316; }

        @keyframes pulse-ring {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0;
          }
        }

        /* Responsive */
        @media (max-width: 359px) {
          .icon-bubble {
            width: 36px;
            height: 36px;
          }
          .nav-label {
            font-size: 8px;
          }
          .float-nav {
            left: 6px;
            right: 6px;
            padding: 12px 4px 12px;
            gap: 10px;
          }
          .nav-btn {
            gap: 3px;
          }
        }

        @media (min-width: 415px) {
          .icon-bubble {
            width: 48px;
            height: 48px;
          }
          .nav-label {
            font-size: 11px;
          }
          .float-nav {
            left: 16px;
            right: 16px;
            padding: 16px 8px 16px;
            gap: 18px;
          }
          .nav-btn {
            gap: 5px;
          }
        }

        /* Hide on desktop (768px and above) */
        @media (min-width: 768px) {
          .float-nav {
            display: none;
          }
        }
      `}</style>

      <div className={`float-nav ${(shouldHide || isScrolling) ? 'hidden' : ''}`}>
        {/* Home */}
        <button
          className="nav-btn"
          onClick={() => handleTabClick('home', '/')}
        >
          <div className={`icon-bubble ${activeTab === 'home' ? 'active home' : 'inactive home-inactive'}`}>
            {notifications.home && activeTab !== 'home' && (
              <div className="notification-dot dot-home" />
            )}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                stroke={activeTab === 'home' ? '#ffffff' : 'rgba(13,18,33,0.6)'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 22V12h6v10"
                stroke={activeTab === 'home' ? '#ffffff' : 'rgba(13,18,33,0.6)'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className={`nav-label ${activeTab === 'home' ? 'active' : 'inactive home-label'}`}>
            Home
          </span>
        </button>

        {/* Student Offers → Scholarships */}
        <button
          className="nav-btn"
          onClick={() => {
            localStorage.setItem('scholarships_last_seen', new Date().toISOString());
            setHasNewScholarships(false);
            handleTabClick('offers', '/scholarships');
          }}
        >
          <div className={`icon-bubble ${activeTab === 'offers' ? 'active offers' : 'inactive offers-inactive'}`}>
            {hasNewScholarships && activeTab !== 'offers' && (
              <div className="notification-dot dot-offers" />
            )}
            {/* Globe icon */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke={activeTab === 'offers' ? '#ffffff' : 'rgba(245,197,24,0.9)'} strokeWidth="2"/>
              <path d="M12 3a14 14 0 014 9 14 14 0 01-4 9 14 14 0 01-4-9 14 14 0 014-9z" stroke={activeTab === 'offers' ? '#ffffff' : 'rgba(245,197,24,0.9)'} strokeWidth="1.5"/>
              <path d="M3.5 9h17M3.5 15h17" stroke={activeTab === 'offers' ? '#ffffff' : 'rgba(245,197,24,0.9)'} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <span className={`nav-label ${activeTab === 'offers' ? 'active' : 'inactive offers-label'}`}>
            Scholarships
          </span>
        </button>

        {/* Ask AI */}
        <button
          className="nav-btn"
          onClick={() => handleTabClick('ai', '/ai-advisor')}
        >
          <div className={`icon-bubble ${activeTab === 'ai' ? 'active ai' : 'inactive ai-inactive'}`}>
            {notifications.ai && activeTab !== 'ai' && (
              <div className="notification-dot dot-ai" />
            )}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Option 4: Lightning Bolt - Power & Speed */}
              <path 
                d="M13 3L4 14H11L10 21L19 10H12L13 3Z" 
                fill={activeTab === 'ai' ? '#ffffff' : 'rgba(14,165,176,1)'} 
                stroke={activeTab === 'ai' ? '#ffffff' : 'rgba(14,165,176,1)'} 
                strokeWidth="1.5" 
                strokeLinejoin="round" 
              />
            </svg>
          </div>
          <span className={`nav-label ${activeTab === 'ai' ? 'active' : 'inactive ai-label'}`}>
            Ask AI
          </span>
        </button>

        {/* Friends */}
        <button
          className="nav-btn"
          onClick={handleFriendsClick}
        >
          <div className={`icon-bubble ${activeTab === 'friends' ? 'active friends' : 'inactive friends-inactive'}`}>
            {notifications.friends && activeTab !== 'friends' && (
              <div className="notification-dot dot-friends" />
            )}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Option 6: User Plus (Add Friend) */}
              {/* Person */}
              <circle
                cx="9"
                cy="8"
                r="4"
                stroke={activeTab === 'friends' ? '#ffffff' : 'rgba(249,115,22,0.9)'}
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"
                stroke={activeTab === 'friends' ? '#ffffff' : 'rgba(249,115,22,0.9)'}
                strokeWidth="2"
                strokeLinecap="round"
                fill="none"
              />
              {/* Plus sign */}
              <line
                x1="18"
                y1="8"
                x2="22"
                y2="8"
                stroke={activeTab === 'friends' ? '#ffffff' : 'rgba(249,115,22,0.9)'}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <line
                x1="20"
                y1="6"
                x2="20"
                y2="10"
                stroke={activeTab === 'friends' ? '#ffffff' : 'rgba(249,115,22,0.9)'}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <span className={`nav-label ${activeTab === 'friends' ? 'active' : 'inactive friends-label'}`}>
            Friends
          </span>
        </button>
      </div>
    </>
  );
};

export default BottomNav;
